/**
* TQL - Terra Query Language
*
* A powerful query language for filtering HTTP requests in Terra Portal.
*
* Syntax examples:
*   req.method.eq("POST")                    - Method equals POST
*   req.path.cont("/api")                    - Path contains "/api"
*   req.host.eq("api.example.com")           - Host equals domain
*   req.query.cont("token")                  - Query string contains "token"
*   req.header["Authorization"].exists()     - Has Authorization header
*   req.header["Content-Type"].cont("json")  - Content-Type contains json
*   resp.code.gte(400)                       - Status >= 400 (errors)
*   resp.code.lt(300)                        - Status < 300 (success)
*   resp.header["Set-Cookie"].exists()       - Response has Set-Cookie
*   resp.body.cont("error")                  - Response body contains "error"
*   resp.roundtrip.gt(1000)                  - Response time > 1000ms
*   req.raw.cont("password")                 - Raw request contains "password"
*   preset:hide-static                       - Apply saved preset
*
* Logical operators:
*   expr AND expr                   - Both must match
*   expr OR expr                    - Either must match
*   NOT expr                        - Negate expression
*   (expr)                          - Grouping
*/
export type TqlField =
// Request fields
| 'req.method'
| 'req.path'
| 'req.query'
| 'req.host'
| 'req.port'
| 'req.body'
| 'req.raw'
| 'req.ext'
// Response fields
| 'resp.code'
| 'resp.body'
| 'resp.raw'
| 'resp.roundtrip'
| 'resp.length'
| 'resp.mime'
// Header access (special syntax: req.header["name"] or resp.header["name"])
| 'req.header'
| 'resp.header'
// Metadata
| 'id'
| 'timestamp'
| 'comment'
| 'tag'
| 'highlight';
export type StringOperator =
| 'eq'
| 'neq'
| 'cont'
| 'ncont'
| 'starts'
| 'ends'
| 'regex';
export type NumericOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';
export type ExistenceOperator = 'exists' | 'nexists';
export type LogicalOperator = 'AND' | 'OR' | 'NOT';
export type TqlAstNode =
| TqlFieldExpression
| TqlHeaderExpression
| TqlPresetReference
| TqlLogicalExpression
| TqlNotExpression
| TqlGroupExpression;
export type TqlFieldExpression = {
type: 'field';
field: TqlField;
operator: StringOperator | NumericOperator | ExistenceOperator;
value?: string | number | boolean;
caseSensitive?: boolean;
};
export type TqlHeaderExpression = {
type: 'header';
target: 'req' | 'resp';
headerName: string;
operator: StringOperator | ExistenceOperator;
value?: string;
caseSensitive?: boolean;
};
export type TqlPresetReference = {
type: 'preset';
presetName: string;
};
export type TqlLogicalExpression = {
type: 'logical';
operator: 'AND' | 'OR';
left: TqlAstNode;
right: TqlAstNode;
};
export type TqlNotExpression = {
type: 'not';
expression: TqlAstNode;
};
export type TqlGroupExpression = {
type: 'group';
expression: TqlAstNode;
};
export type TqlParseResult =
| { success: true; ast: TqlAstNode }
| { success: false; error: string; position?: number };
export type FilterPreset = {
id: string;
name: string;
alias: string;
description?: string;
query: string;
isBuiltIn?: boolean;
createdAt: number;
updatedAt: number;
};
export type TqlSuggestion = {
type: 'field' | 'operator' | 'value' | 'preset' | 'keyword';
label: string;
insertText: string;
description?: string;
detail?: string;
};
// Field metadata for autocomplete
export type TqlFieldMeta = {
field: TqlField;
label: string;
description: string;
operators: (StringOperator | NumericOperator | ExistenceOperator)[];
valueType: 'string' | 'number' | 'boolean' | 'none';
examples?: string[];
};
// All available fields with metadata
export const TQL_FIELDS: TqlFieldMeta[] = [
// Request fields
{
field: 'req.method',
label: 'Method',
description: 'HTTP method (GET, POST, PUT, DELETE, etc.)',
operators: ['eq', 'neq'],
valueType: 'string',
examples: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'],
},
{
field: 'req.path',
label: 'Path',
description: 'URL path without query string',
operators: ['eq', 'neq', 'cont', 'ncont', 'starts', 'ends', 'regex'],
valueType: 'string',
examples: ['/api', '/login', '/admin', '/users'],
},
{
field: 'req.query',
label: 'Query String',
description: 'URL query parameters',
operators: ['cont', 'ncont', 'regex', 'exists', 'nexists'],
valueType: 'string',
examples: ['token=', 'page=', 'id='],
},
{
field: 'req.host',
label: 'Host',
description: 'Request host/domain',
operators: ['eq', 'neq', 'cont', 'ncont', 'ends'],
valueType: 'string',
examples: ['api.example.com', 'localhost'],
},
{
field: 'req.port',
label: 'Port',
description: 'Destination port number',
operators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
valueType: 'number',
examples: ['80', '443', '8080', '3000'],
},
{
field: 'req.body',
label: 'Request Body',
description: 'Request body content',
operators: ['cont', 'ncont', 'regex', 'exists', 'nexists'],
valueType: 'string',
},
{
field: 'req.raw',
label: 'Raw Request',
description: 'Complete raw request (headers + body)',
operators: ['cont', 'ncont', 'regex'],
valueType: 'string',
},
{
field: 'req.ext',
label: 'File Extension',
description: 'File extension from URL path',
operators: ['eq', 'neq'],
valueType: 'string',
examples: ['.js', '.css', '.json', '.html', '.png', '.svg'],
},
// Response fields
{
field: 'resp.code',
label: 'Status Code',
description: 'HTTP response status code',
operators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
valueType: 'number',
examples: ['200', '201', '301', '400', '401', '403', '404', '500'],
},
{
field: 'resp.body',
label: 'Response Body',
description: 'Response body content',
operators: ['cont', 'ncont', 'regex', 'exists', 'nexists'],
valueType: 'string',
},
{
field: 'resp.raw',
label: 'Raw Response',
description: 'Complete raw response (headers + body)',
operators: ['cont', 'ncont', 'regex'],
valueType: 'string',
},
{
field: 'resp.roundtrip',
label: 'Round Trip Time',
description: 'Response time in milliseconds',
operators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
valueType: 'number',
examples: ['100', '500', '1000', '5000'],
},
{
field: 'resp.length',
label: 'Response Length',
description: 'Response body size in bytes',
operators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
valueType: 'number',
},
{
field: 'resp.mime',
label: 'MIME Type',
description: 'Response Content-Type',
operators: ['eq', 'neq', 'cont', 'ncont', 'starts'],
valueType: 'string',
examples: ['application/json', 'text/html', 'text/javascript', 'image/'],
},
// Metadata fields
{
field: 'comment',
label: 'Comment',
description: 'Request annotation comment',
operators: ['cont', 'ncont', 'exists', 'nexists'],
valueType: 'string',
},
{
field: 'tag',
label: 'Tag',
description: 'Request tags',
operators: ['eq', 'cont', 'exists', 'nexists'],
valueType: 'string',
},
{
field: 'highlight',
label: 'Highlight Color',
description: 'Row highlight color',
operators: ['eq', 'exists', 'nexists'],
valueType: 'string',
examples: ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'gray'],
},
];
// Operator metadata for autocomplete
export const OPERATOR_LABELS: Record<
StringOperator | NumericOperator | ExistenceOperator,
{ label: string; description: string }
> = {
eq: { label: 'eq', description: 'Equals' },
neq: { label: 'neq', description: 'Not equals' },
cont: { label: 'cont', description: 'Contains' },
ncont: { label: 'ncont', description: 'Not contains' },
starts: { label: 'starts', description: 'Starts with' },
ends: { label: 'ends', description: 'Ends with' },
regex: { label: 'regex', description: 'Regex match' },
gt: { label: 'gt', description: 'Greater than' },
gte: { label: 'gte', description: 'Greater than or equal' },
lt: { label: 'lt', description: 'Less than' },
lte: { label: 'lte', description: 'Less than or equal' },
exists: { label: 'exists', description: 'Field exists' },
nexists: { label: 'nexists', description: 'Field does not exist' },
};
// Built-in presets
export const BUILTIN_PRESETS: Omit<
FilterPreset,
'id' | 'createdAt' | 'updatedAt'
>[] = [
{
name: 'Hide Static Assets',
alias: 'hide-static',
description: 'Hide common static files (JS, CSS, images, fonts)',
query:
'NOT (req.ext.eq(".js") OR req.ext.eq(".css") OR req.ext.eq(".png") OR req.ext.eq(".jpg") OR req.ext.eq(".gif") OR req.ext.eq(".svg") OR req.ext.eq(".woff") OR req.ext.eq(".woff2") OR req.ext.eq(".ico"))',
isBuiltIn: true,
},
{
name: 'API Only',
alias: 'api-only',
description: 'Show only JSON API responses',
query: 'resp.mime.cont("application/json")',
isBuiltIn: true,
},
{
name: 'Errors Only',
alias: 'errors',
description: 'Show only 4xx and 5xx responses',
query: 'resp.code.gte(400)',
isBuiltIn: true,
},
{
name: 'Client Errors',
alias: 'client-errors',
description: 'Show only 4xx responses',
query: 'resp.code.gte(400) AND resp.code.lt(500)',
isBuiltIn: true,
},
{
name: 'Server Errors',
alias: 'server-errors',
description: 'Show only 5xx responses',
query: 'resp.code.gte(500)',
isBuiltIn: true,
},
{
name: 'POST Requests',
alias: 'posts',
description: 'Show only POST requests',
query: 'req.method.eq("POST")',
isBuiltIn: true,
},
{
name: 'With Body',
alias: 'with-body',
description: 'Requests with a body',
query: 'req.body.exists()',
isBuiltIn: true,
},
{
name: 'Has Comments',
alias: 'commented',
description: 'Requests with comments',
query: 'comment.exists()',
isBuiltIn: true,
},
{
name: 'Tagged',
alias: 'tagged',
description: 'Requests with tags',
query: 'tag.exists()',
isBuiltIn: true,
},
{
name: 'Highlighted',
alias: 'highlighted',
description: 'Highlighted requests',
query: 'highlight.exists()',
isBuiltIn: true,
},
];
